import {useEffect} from 'react';

/**
 * iOS WKWebView/Safari 에서 input/textarea/select 에 focus 가 잡힐 때
 * 해당 요소를 화면 중앙으로 즉시 스크롤한다.
 *
 * - focus 즉시 스크롤 → 빠른 반응
 * - 키보드가 완전히 올라온 뒤 scroll-padding-bottom 을 키보드 높이에 맞게 보정하고
 *   한 번 더 scrollIntoView → bottom-btn 과의 겹침 방지
 *
 * --keyboard-height CSS 변수는 scroll-padding 에만 사용.
 * bottom-btn 위치는 건드리지 않음 (iOS 가 fixed 요소를 키보드 위로 자동으로 올려줌).
 *
 * 안드로이드는 OS 가 자동으로 처리해주므로 동작하지 않음.
 */
const isIOS = (): boolean => {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
    const ua = navigator.userAgent;
    const isIPadOS = ua.includes('Macintosh') && 'ontouchend' in document;
    return /iPad|iPhone|iPod/.test(ua) || isIPadOS;
};

const FOCUSABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

const scrollIntoCenter = (el: HTMLElement) => {
    try {
        el.scrollIntoView({block: 'center', behavior: 'auto'});
    } catch {
        el.scrollIntoView();
    }
};

export function useIosInputAutoScroll(): void {
    useEffect(() => {
        if (!isIOS()) return;
        if (typeof document === 'undefined') return;

        let activeEl: HTMLElement | null = null;
        let debounceTimer: ReturnType<typeof setTimeout> | null = null;

        const handleFocusIn = (e: FocusEvent) => {
            const target = e.target as HTMLElement | null;
            if (!target || !FOCUSABLE_TAGS.has(target.tagName)) return;
            if (target instanceof HTMLInputElement) {
                if (target.type === 'hidden' || target.readOnly) return;
            }
            activeEl = target;
            // 키보드 올라오기 전 즉시 스크롤 → 빠른 반응
            scrollIntoCenter(target);
        };

        const handleFocusOut = () => {
            activeEl = null;
            // 키보드 내려갈 때 scroll-padding 초기화
            document.documentElement.style.setProperty('--keyboard-height', '0px');
        };

        const handleViewportResize = () => {
            // 키보드 높이를 CSS 변수로 설정 (scroll-padding-bottom 에서 사용)
            const vv = window.visualViewport;
            if (vv) {
                const kbHeight = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
                document.documentElement.style.setProperty('--keyboard-height', `${kbHeight}px`);
            }

            // 키보드 애니메이션이 끝난 뒤 한 번만 보정 스크롤 (50ms debounce)
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                if (activeEl && document.activeElement === activeEl) {
                    scrollIntoCenter(activeEl);
                }
            }, 50);
        };

        document.addEventListener('focusin', handleFocusIn);
        document.addEventListener('focusout', handleFocusOut);
        window.visualViewport?.addEventListener('resize', handleViewportResize);

        return () => {
            document.removeEventListener('focusin', handleFocusIn);
            document.removeEventListener('focusout', handleFocusOut);
            window.visualViewport?.removeEventListener('resize', handleViewportResize);
            if (debounceTimer) clearTimeout(debounceTimer);
            document.documentElement.style.removeProperty('--keyboard-height');
        };
    }, []);
}

export default useIosInputAutoScroll;
